import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import GameHeader from './components/GameHeader';
import GameArea from './components/GameArea';
import GameControls from './components/GameControls';
import GameOverModal from './components/GameOverModal';
import Instructions from './components/Instructions';

function App() {
  // Game constants
  const CAR_START_POSITION = 5;
  const CLIENT_POSITION = 100; // Much longer road for better scrolling (doubled)
  const TAP_PUSH_AMOUNT = 1; // Smaller movement per tap for smoother scrolling
  const MAX_SPEED = 10; // Maximum TPS
  const SPEED_DECAY = 0.95;
  
  const [gameState, setGameState] = useState('idle'); // idle, playing, won
  const [tapCount, setTapCount] = useState(0);
  const [carPosition, setCarPosition] = useState(CAR_START_POSITION); // percentage from left
  const [speed, setSpeed] = useState(0);
  const [gameStartTime, setGameStartTime] = useState(0);
  const [gameEndTime, setGameEndTime] = useState(0);
  
  const lastTapTime = useRef(0);
  const animationId = useRef(null);

  // Reset game state
  const resetGame = useCallback(() => {
    setGameState('idle');
    setTapCount(0);
    setCarPosition(CAR_START_POSITION);
    setSpeed(0);
    setGameStartTime(0);
    setGameEndTime(0);
    lastTapTime.current = 0;
    
    if (animationId.current) {
      cancelAnimationFrame(animationId.current);
      animationId.current = null;
    }
  }, []);

  // Handle tap events
  const handleTap = useCallback((event) => {
    if (gameState !== 'playing') return;
    
    event.preventDefault();
    
    const currentTime = Date.now();
    setTapCount(prev => prev + 1);
    
    // Calculate speed based on taps per second
    if (lastTapTime.current > 0) {
      const tapInterval = currentTime - lastTapTime.current;
      // Convert to TPS: (1000ms / interval) = taps per second
      const newSpeed = Math.min(MAX_SPEED, 1000 / tapInterval);
      setSpeed(newSpeed);
    }
    lastTapTime.current = currentTime;
    
    // Move car forward
    setCarPosition(prev => {
      const newPosition = prev + TAP_PUSH_AMOUNT;
      
      // Log car movement for debugging
      console.log('=== CAR MOVEMENT DEBUG ===');
      console.log(`Previous position: ${prev}%`);
      console.log(`TAP_PUSH_AMOUNT: ${TAP_PUSH_AMOUNT}%`);
      console.log(`New position: ${newPosition}%`);
      console.log(`CLIENT_POSITION: ${CLIENT_POSITION}%`);
      console.log(`Will win: ${newPosition >= CLIENT_POSITION}`);
      console.log('==========================');
      
      if (newPosition >= CLIENT_POSITION) {
        winGame(currentTime);
        return CLIENT_POSITION;
      }
      return newPosition;
    });
  }, [gameState]);

  // Start game
  const startGame = useCallback(() => {
    if (gameState === 'playing') return;
    
    setGameState('playing');
    setGameStartTime(Date.now());
    startSpeedDecay();
  }, [gameState]);

  // Start speed decay animation
  const startSpeedDecay = useCallback(() => {
    const decaySpeed = () => {
      if (gameState === 'playing') {
        setSpeed(prev => {
          const newSpeed = prev * SPEED_DECAY;
          return newSpeed < 1 ? 0 : newSpeed;
        });
        animationId.current = requestAnimationFrame(decaySpeed);
      }
    };
    decaySpeed();
  }, [gameState]);

  // Win game
  const winGame = useCallback((endTime) => {
    setGameState('won');
    setGameEndTime(endTime);
    
    if (animationId.current) {
      cancelAnimationFrame(animationId.current);
      animationId.current = null;
    }
  }, []);

  // Calculate progress percentage
  const getProgress = useCallback(() => {
    const totalDistance = CLIENT_POSITION - CAR_START_POSITION;
    const currentDistance = carPosition - CAR_START_POSITION;
    const progress = Math.max(0, Math.min(100, (currentDistance / totalDistance) * 100));
    
    // Log progress calculation for debugging
    console.log('=== PROGRESS DEBUG ===');
    console.log(`totalDistance: ${totalDistance}%`);
    console.log(`currentDistance: ${currentDistance}%`);
    console.log(`progress: ${progress}%`);
    console.log('======================');
    
    return progress;
  }, [carPosition]);

  // Calculate how far the car has moved in the visible area
  const getVisibleProgress = useCallback(() => {
    // Show progress based on visible area (0-100% of screen)
    const visibleDistance = 100; // 100% of screen width
    const currentVisibleDistance = carPosition - CAR_START_POSITION;
    const visibleProgress = Math.max(0, Math.min(100, (currentVisibleDistance / visibleDistance) * 100));
    
    // Log visible progress for debugging
    console.log('=== VISIBLE PROGRESS DEBUG ===');
    console.log(`carPosition: ${carPosition}%`);
    console.log(`CAR_START_POSITION: ${CAR_START_POSITION}%`);
    console.log(`currentVisibleDistance: ${currentVisibleDistance}%`);
    console.log(`visibleProgress: ${visibleProgress}%`);
    console.log('=============================');
    
    return visibleProgress;
  }, [carPosition]);

  // Calculate road scroll position for side-scrolling effect
  const getRoadScrollPosition = useCallback(() => {
    // When car reaches 50% of screen, start scrolling the road
    const scrollThreshold = 50;
    if (carPosition <= scrollThreshold) {
      return 0; // No scroll at the beginning
    }
    // Calculate how much to scroll the road left
    const scrollAmount = carPosition - scrollThreshold;
    // Allow road to scroll enough to show the client at the end
    // When car reaches 100%, road should scroll 50% to show client at right edge
    const maxScroll = 50; // Maximum scroll amount
    const finalScroll = Math.min(scrollAmount, maxScroll);
    
    // Log road scroll for debugging
    console.log('=== ROAD SCROLL DEBUG ===');
    console.log(`carPosition: ${carPosition}%`);
    console.log(`scrollThreshold: ${scrollThreshold}%`);
    console.log(`scrollAmount: ${scrollAmount}%`);
    console.log(`maxScroll: ${maxScroll}%`);
    console.log(`finalScroll: ${finalScroll}%`);
    console.log('========================');
    
    return finalScroll;
  }, [carPosition]);

  // Calculate car's visual position (stays in view while road scrolls)
  const getCarVisualPosition = useCallback(() => {
    let visualPosition;
    if (carPosition <= 50) {
      visualPosition = carPosition; // Normal positioning before scrolling starts
    } else {
      // After scrolling starts, keep car at 50% (center of visible area)
      visualPosition = 50;
    }
    
    
    return visualPosition;
  }, [carPosition]);

  // Calculate final stats
  const getFinalStats = useCallback(() => {
    if (!gameStartTime || !gameEndTime) return { time: 0, taps: 0, avgSpeed: 0 };
    
    const gameTime = (gameEndTime - gameStartTime) / 1000;
    return {
      time: gameTime.toFixed(1),
      taps: tapCount,
      avgSpeed: Math.round(speed)
    };
  }, [gameStartTime, gameEndTime, tapCount, speed]);

  // Reset game on mount (page refresh)
  useEffect(() => {
    // Ensure game starts in proper initial state
    setGameState('idle');
    setTapCount(0);
    setCarPosition(CAR_START_POSITION);
    setSpeed(0);
    setGameStartTime(0);
    setGameEndTime(0);
    lastTapTime.current = 0;
  }, []); // Empty dependency array means this runs once on mount

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationId.current) {
        cancelAnimationFrame(animationId.current);
      }
    };
  }, []);

  // Handle visibility change to pause game when tab is not active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && gameState === 'playing') {
        setGameState('paused');
        if (animationId.current) {
          cancelAnimationFrame(animationId.current);
          animationId.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [gameState]);

  return (
    <div className="App">
      <div className="game-container">
        <GameHeader tapCount={tapCount} speed={speed} />
        
        <GameArea
          carPosition={carPosition}
          carVisualPosition={getCarVisualPosition()}
          gameState={gameState}
          onTap={handleTap}
          progress={getProgress()}
          visibleProgress={getVisibleProgress()}
          roadScrollPosition={getRoadScrollPosition()}
        />
        
        <GameControls
          gameState={gameState}
          onStart={startGame}
          onReset={resetGame}
        />
        
        {gameState === 'won' && (
          <GameOverModal
            stats={getFinalStats()}
            onPlayAgain={resetGame}
          />
        )}
        
        <Instructions />
      </div>
    </div>
  );
}

export default App;
